import { storageRef } from '../../Firebase';

/**
 * Convert base64 string to Blob
 */
function base64ToBlob(base64, mimeType = 'image/png') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/**
 * Get file extension from mime type
 */
function getExtensionFromMimeType(mimeType) {
  const extensions = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
  };
  return extensions[mimeType] || 'png';
}

/**
 * Upload a base64 encoded image to Firebase Storage
 * 
 * @param {string} base64Data - The base64 encoded image data (without data URL prefix)
 * @param {string} mimeType - The MIME type of the image
 * @param {string} shopId - The shop ID for organizing storage
 * @param {string} chatId - The chat ID for organizing storage
 * @param {number} messageIndex - The message index within the chat
 * @returns {Promise<{downloadUrl: string, storagePath: string}>}
 */
export async function uploadGeneratedImage(base64Data, mimeType, shopId, chatId, messageIndex) {
  // Generate unique filename with unique ID to avoid collisions
  const extension = getExtensionFromMimeType(mimeType);
  const uniqueId = `ai_${chatId}_${messageIndex}_${Date.now()}`;
  const filename = `${uniqueId}.${extension}`;
  const storagePath = `${shopId}/${filename}`;
  
  // Convert base64 to blob
  const blob = base64ToBlob(base64Data, mimeType);
  
  // Create storage reference
  const fileRef = storageRef.child(storagePath);
  
  // Upload the file
  const snapshot = await fileRef.put(blob, {
    contentType: mimeType,
    customMetadata: {
      chatId,
      messageIndex: String(messageIndex),
      generatedAt: new Date().toISOString(),
    },
  });
  
  // Get the download URL
  const downloadUrl = await snapshot.ref.getDownloadURL();
  
  return {
    downloadUrl,
    storagePath,
  };
}

/**
 * Upload a generated image and create a photo object compatible with the existing system
 * This creates a photo object that can be used as the main product photo
 * 
 * @param {string} base64Data - The base64 encoded image data
 * @param {string} mimeType - The MIME type of the image
 * @param {string} shopId - The shop ID
 * @returns {Promise<{photo: object, downloadUrl: string, storagePath: string}>}
 */
export async function uploadAsProductPhoto(base64Data, mimeType, shopId) {
  const extension = getExtensionFromMimeType(mimeType);
  const uniqueId = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const filename = `${uniqueId}.${extension}`;
  const storagePath = `${shopId}/${filename}`;
  
  // Convert base64 to blob
  const blob = base64ToBlob(base64Data, mimeType);
  
  // Create storage reference
  const fileRef = storageRef.child(storagePath);
  
  // Upload the file
  const snapshot = await fileRef.put(blob, {
    contentType: mimeType,
    customMetadata: {
      source: 'ai-generated',
      generatedAt: new Date().toISOString(),
    },
  });
  
  // Get the download URL
  const downloadUrl = await snapshot.ref.getDownloadURL();
  
  // Create photo object compatible with the existing system
  // type: 'g' means GCS/imgix compatible
  const photo = {
    type: 'g',
    s: shopId,        // shopId
    id: uniqueId,     // unique identifier
    e: extension,     // extension
    url: downloadUrl, // fallback URL
    // These will be populated by imgix when served
    // width, height, alt can be set later
  };
  
  return {
    photo,
    downloadUrl,
    storagePath,
  };
}

export default uploadGeneratedImage;
